import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getTeachingCourses, createCourse } from "../courses";

const LANGUAGES = [
    { code: "de-DE", labelKey: "German" },
    { code: "en-GB", labelKey: "English" },
    { code: "fr-FR", labelKey: "French" },
];

// Teacher home: list of own courses (join code, sizes) + create-course form.
const TeachPage = () => {
    const { t } = useTranslation();
    const [courses, setCourses] = useState(null);
    const [title, setTitle] = useState("");
    const [language, setLanguage] = useState("de-DE");
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState(null);

    const loadCourses = () => {
        getTeachingCourses()
            .then(setCourses)
            .catch((err) => setError(err.message));
    };

    useEffect(loadCourses, []);

    const handleCreate = (e) => {
        e.preventDefault();
        if (!title.trim() || creating) return;
        setCreating(true);
        setError(null);
        createCourse(title.trim(), language)
            .then(() => {
                setTitle("");
                loadCourses();
            })
            .catch((err) => setError(err.message))
            .finally(() => setCreating(false));
    };

    return (
        <div className="px-4 pt-4 md:px-20 md:pt-10 flex flex-col gap-4 pb-10">
            <h1 className="text-2xl md:text-3xl font-semibold">{t("teach.title")}</h1>

            {/* Create course */}
            <form
                onSubmit={handleCreate}
                className="rounded-lg border border-border bg-secondary p-4 md:p-6 flex flex-wrap items-end gap-3"
            >
                <div className="flex flex-col gap-1 grow min-w-48">
                    <label htmlFor="course-title" className="text-sm text-gray-600">
                        {t("teach.courseTitle")}
                    </label>
                    <input
                        id="course-title"
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder={t("teach.courseTitlePlaceholder")}
                        className="rounded-md border border-border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                </div>
                <div className="flex flex-col gap-1">
                    <label htmlFor="course-language" className="text-sm text-gray-600">
                        {t("language")}
                    </label>
                    <select
                        id="course-language"
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                        className="rounded-md border border-border px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                        {LANGUAGES.map((lang) => (
                            <option key={lang.code} value={lang.code}>
                                {t(lang.labelKey)}
                            </option>
                        ))}
                    </select>
                </div>
                <button
                    type="submit"
                    disabled={creating || !title.trim()}
                    className="rounded-md bg-primary px-4 py-2 text-white hover:bg-purple-600 disabled:opacity-50"
                >
                    {t("teach.createCourse")}
                </button>
            </form>

            {error && <p className="text-sm text-red-600">{error}</p>}

            {courses === null ? (
                <div className="h-24 animate-pulse rounded bg-gray-200" />
            ) : courses.length === 0 ? (
                <p className="text-gray-600">{t("teach.noCourses")}</p>
            ) : (
                <ul className="flex flex-col gap-3">
                    {courses.map((course) => (
                        <li key={course.id}>
                            <Link
                                to={`/teach/${course.id}`}
                                className="block rounded-lg border border-border bg-secondary p-4 hover:bg-gray-100 transition-colors"
                            >
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                    <div>
                                        <span className="font-medium">{course.title}</span>
                                        <span className="ml-2 text-xs text-gray-500">
                                            {t(
                                                LANGUAGES.find((l) => l.code === course.language)
                                                    ?.labelKey || course.language
                                            )}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-4 text-sm text-gray-600">
                                        <span>
                                            {course.student_count} {t("teach.students")}
                                        </span>
                                        <span>
                                            {course.sentence_count} {t("courses.sentences")}
                                        </span>
                                        <span className="rounded bg-purple-100 px-2 py-0.5 font-mono font-semibold tracking-widest text-purple-800">
                                            {course.join_code}
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default TeachPage;
